import React, { useEffect, useState, useRef, useContext } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import {
  Card,
  Stack,
  Button,
  MenuItem,
  Select,
  Typography,
} from '@mui/material';
import SearchList from '../table-elements/search-list.component';
import ListData from '../table-elements/list-data.component';
import Iconify from '../common/iconify.component';
import {
  LISTING_COLUMNS,
  LISTING_ACTIONS,
} from '../../config/module-configs/datachangelogs.config';
import {
  getData,
  getListDataChangeLogs,
  getUserName,
} from '../../services/reports.service';
import useFilteredActions from '../../hooks/filtered-action.hook';
import { AccessContext } from '../../context/access.context';
import { ACTIONS } from '../../config/const.config';
import ViewDataChangeLogs from './view-data-change-logs.component';
import PAGE_TITLE_LIST from '../../config/page-title.config';

const ListDataChangeLogs = () => {
  const columns = LISTING_COLUMNS;

  const [options, setOptions] = useState({
    page: 0,
    rowsPerPage: 10,
    totalRows: 0,
    rows: [],
    reloadCounter: 0,
    sortBy: '',
    sortOrder: '',
    error: false,
    loading: true,
  });

  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const id = searchParams.get('id');
  const dataType = searchParams.get('data');
  const [value, setValue] = useState('');
  const [admin, setAdmin] = useState([]);
  const [data, setData] = useState([]);
  const [dataValue, setdataValue] = useState(dataType || '');

  const [heading, setHeading] = useState('');

  const { accessData } = useContext(AccessContext);
  const actions = useFilteredActions(
    accessData.role,
    'data_change_logs',
    LISTING_ACTIONS
  );
  const [dataName, setDataName] = useState('');

  const searchSubscribersRef = useRef(null);
  const [doAction, setDoAction] = useState({
    data: null,
    action: null,
  });

  const handlePageChange = (page) => {
    setOptions((prev) => ({
      ...prev,
      page,
      reloadCounter: prev.reloadCounter + 1,
      loading: true,
    }));
  };

  const handleRowsPerPageChange = (rowsPerPage) => {
    setOptions((prev) => ({
      ...prev,
      rowsPerPage,
      page: 0,
      reloadCounter: prev.reloadCounter + 1,
      loading: true,
    }));
  };

  const handleSortChange = (sortBy, sortOrder) => {
    setOptions((prev) => ({
      ...prev,
      sortBy,
      sortOrder,
      reloadCounter: prev.reloadCounter + 1,
      loading: true,
    }));
  };

  const handleSearchChange = (e) => {
    setOptions({
      ...options,
      searchValue: e.target.value || '',
    });
  };

  const handleSearchSubscribers = () => {
    if (options.loading) return;
    setOptions({
      ...options,
      q: searchSubscribersRef.current.value,
      reloadCounter: options.reloadCounter + 1,
      page: 0,
      loading: true,
    });
  };

  const handleClearSearch = () => {
    if (options.loading) return;
    setOptions({
      ...options,
      searchValue: '',
      q: null,
      reloadCounter: options.reloadCounter + 1,
      loading: true,
    });
    searchSubscribersRef.current.value = '';
  };

  const toReadableFormat = (text) => {
    if (!text) return '';
    if (text === 'feed') {
      return PAGE_TITLE_LIST.GROUPS;
    }
    return text
      .replace(/[-_]/g, ' ')
      .split(' ')
      .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
      .join(' ');
  };

  const handleActionSelect = (selectedAction, selectedData) => {
    setDoAction({
      ...doAction,
      data: selectedData || null,
      action: selectedAction || null,
    });
  };
  const handleActionCancel = () => {
    setDoAction({
      ...doAction,
      data: null,
      action: null,
    });
  };

  const handleMenuChange = (e) => {
    setValue(e.target.value);
    setOptions((prev) => ({
      ...prev,
      page: 0,
      reloadCounter: prev.reloadCounter + 1,
      loading: true,
    }));
  };
  const handleDataChange = (e) => {
    setdataValue(e.target.value);
    setOptions((prev) => ({
      ...prev,
      page: 0,
      reloadCounter: prev.reloadCounter + 1,
      loading: true,
    }));
  };

  const handleBack = () => {
    navigate(-1);
  };

  useEffect(() => {
    let params = `?page=${options.page + 1}`;
    params += `&perPage=${options.rowsPerPage}`;
    if (options.sortBy && options.sortOrder) {
      params += `&sortBy=${options.sortBy}`;
      params += `&sortOrder=${options.sortOrder}`;
    }
    if (options.q) {
      params += `&q=${options.q}`;
    }

    if (value) {
      params += `&adminName=${encodeURIComponent(value)}`;
    }

    if (dataValue) {
      params += `&data=${encodeURIComponent(dataValue)}`;
    }

    if (id) {
      params += `&id=${id}`;
    }

    getListDataChangeLogs(params)
      .then((res) => {
        setOptions({
          ...options,
          loading: false,
          totalRows: res?.data?.totalRows || 0,
          rows: res?.data?.rows || [],
          error: false,
        });
        if (id) {
          setDataName(res.data.selectedDataName);
        }
      })
      .catch(() => {
        setOptions((prev) => ({
          ...prev,
          loading: false,
          rows: [],
          totalRows: 0,
          error: true,
        }));
      });
  }, [options.reloadCounter, value, dataValue]);

  useEffect(() => {
    getUserName()
      .then((res) => {
        setAdmin(res.data);
      })
      .catch(() => {
        setAdmin(PAGE_TITLE_LIST.ERROR_NOT_FOUND);
      });

    getData()
      .then((res) => {
        setData(res.data);
      })
      .catch(() => {
        setData(PAGE_TITLE_LIST.ERROR_NOT_FOUND);
      });
  }, []);

  useEffect(() => {
    let title = '';

    if (dataValue && value && dataName) {
      title = `${PAGE_TITLE_LIST.DATA_CHANGE_LOGS} on ${toReadableFormat(
        dataValue
      )} by ${value} - ${dataName}`;
    } else if (dataValue && dataName) {
      title = `${PAGE_TITLE_LIST.DATA_CHANGE_LOGS}  for ${toReadableFormat(
        dataValue
      )} - ${dataName}`;
    } else if (dataValue && value) {
      title = `${PAGE_TITLE_LIST.DATA_CHANGE_LOGS}  on ${toReadableFormat(
        dataValue
      )} by ${value}`;
    } else if (dataValue) {
      title = `${PAGE_TITLE_LIST.DATA_CHANGE_LOGS} for ${toReadableFormat(
        dataValue
      )}`;
    } else if (value) {
      title = `${PAGE_TITLE_LIST.DATA_CHANGE_LOGS} by ${value}`;
    }

    setHeading(title);
  }, [value, dataValue, dataName]);

  return (
    <>
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        spacing={0}
        sx={{ py: 2, flexWrap: 'wrap' }}
      >
        <div style={{ display: 'none' }}>
          <SearchList
            label="Logs"
            handleSearch={handleSearchSubscribers}
            inputRef={searchSubscribersRef}
            onChange={handleSearchChange}
            options={options}
            clearSearch={handleClearSearch}
            loading={options.loading}
          />
        </div>
        <Stack
          direction="row"
          spacing={2}
          alignItems="center"
          sx={{ flexGrow: 1 }}
        >
          <Select
            value={dataValue}
            onChange={handleDataChange}
            sx={{ mr: 2 }}
            displayEmpty
            disabled={dataType}
          >
            <MenuItem value="">Select Data</MenuItem>
            {(data || []).map((item, index) => (
              <MenuItem key={index} value={item.key.data}>
                {item.data}
              </MenuItem>
            ))}
          </Select>
          <Select value={value} onChange={handleMenuChange} displayEmpty>
            <MenuItem value="">Select Change By</MenuItem>
            {(admin || []).map((item, index) => (
              <MenuItem key={index} value={item.full_name}>
                {item.full_name}
              </MenuItem>
            ))}
          </Select>

          <Typography variant="h6" component="div">
            {heading}
          </Typography>
        </Stack>

        <Stack direction="column" spacing={2}>
          {(id || dataType) && (
            <Button
              color="inherit"
              variant="contained"
              startIcon={<Iconify icon="ic:arrow-back" />}
              onClick={handleBack}
            >
              Back
            </Button>
          )}
        </Stack>
      </Stack>

      <Card>
        <ListData
          columns={columns}
          rows={options.rows}
          page={options.page}
          rowsPerPage={options.rowsPerPage}
          totalRows={options.totalRows}
          loading={options.loading}
          actions={actions}
          error={options.error}
          sortBy={options.sortBy}
          sortOrder={options.sortOrder}
          onPageChange={handlePageChange}
          onRowsPerPageChange={handleRowsPerPageChange}
          onSortChange={handleSortChange}
          onAction={handleActionSelect}
        />
      </Card>

      {doAction.action === ACTIONS.VIEW.value && (
        <ViewDataChangeLogs
          title="Data Change Logs Details"
          user={doAction.data}
          dataId={doAction.data.id}
          onCancel={handleActionCancel}
        />
      )}
    </>
  );
};

export default ListDataChangeLogs;
